import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { switchMap, finalize, catchError } from 'rxjs/operators';

export interface VesTransactionItem {
  id: string;
  userId: string;
  type: string;
  operador: string;
  banco: string | null;
  referenceId: string;
  debitVes: number | null;
  creditVes: number | null;
  balanceVes: number;
  description: string | null;
  status: string;
  operatorEmail: string | null;
  /**
   * `tipo_cuenta === 'Interna'`: la fila la registró un operador, no un
   * cliente. Cuando es true el backend manda `usuario` en null a propósito y
   * la identidad válida es `operatorEmail`.
   */
  esOperador?: boolean;
  createdAt: string;
  userName: string;
  userEmail: string;
  userCountry: string;
  userNationality: string;
  userIdType: string;
  userIdNumber: string;
  isCompany: boolean;
  companyName: string | null;
  companyRut: string | null;

  // Campos enriquecidos para SWAP_USDT_VES, COMPRA_USDT_VES, RETIRO_USDT_VES
  usdtAmount?: number;
  vesRate?: number;

  // Campos adicionales solo para RETIRO_USDT_VES
  walletAddress?: string;
  networkBinance?: string;
  withdrawalFee?: number;
  withdrawalStatus?: string;
  binanceTxId?: string;
}

export interface VesTransactionsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface VesTransactionsResponse {
  success: boolean;
  data: {
    items: VesTransactionItem[];
    pagination: VesTransactionsPagination;
  };
  message?: string;
}

export interface VesTransactionsQueryParams {
  page?: number;
  limit?: number;
  userId?: string;
  type?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

@Injectable({
  providedIn: 'root',
})
export class VesTransactionsService {
  private apiUrl = environment.apiNest;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session || !session.accessToken || !session.accessToken.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    const token = session.accessToken.jwtToken.trim();
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 401:
          errorMessage = 'Error de autenticación. Por favor, inicie sesión nuevamente.';
          break;
        case 403:
          errorMessage = 'No tiene permisos para realizar esta acción.';
          break;
        case 404:
          errorMessage = 'El servicio no está disponible en este momento.';
          break;
        case 422:
          // 422 = validación / regla de negocio (ej: ref sin payin en banco).
          // El backend envía un mensaje específico que el operador debe ver tal cual.
          errorMessage = error.error?.message || 'La operación no se pudo procesar por una regla de validación.';
          break;
        case 500:
          errorMessage = 'Error interno del servidor. Por favor, intente más tarde.';
          break;
        default:
          errorMessage = error.error?.message || errorMessage;
      }
    }
    return throwError(() => ({ message: errorMessage, originalError: error }));
  }

  approveSwap(id: string): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${this.apiUrl}wallet-ves/swaps/${id}/approve`,
          {},
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  rejectSwap(id: string, reason: string): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${this.apiUrl}wallet-ves/swaps/${id}/reject`,
          { reason },
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  approveRetiroVes(id: string): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${this.apiUrl}wallet-ves/retiros-ves/${id}/approve`,
          {},
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  rejectRetiroVes(id: string, reason: string): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${this.apiUrl}wallet-ves/retiros-ves/${id}/reject`,
          { reason },
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  // === Referencias VES Admin ===

  getReferenciasVesAdmin(
    params: { page?: number; limit?: number; userId?: string; status?: string; onlyOperator?: boolean; rail?: 'N58' | 'BANCO_PLAZA' | 'R4' } = {},
  ): Observable<any> {
    let httpParams = new HttpParams();
    if (params.page) httpParams = httpParams.set('page', params.page.toString());
    if (params.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params.userId) httpParams = httpParams.set('userId', params.userId);
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.onlyOperator !== undefined) httpParams = httpParams.set('onlyOperator', params.onlyOperator ? 'true' : 'false');
    if (params.rail) httpParams = httpParams.set('rail', params.rail);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<any>(
          `${this.apiUrl}wallet-ves/referencias-ves`,
          { headers, params: httpParams },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  registrarReferenciaOperador(payload: { rail?: 'N58' | 'BANCO_PLAZA' | 'R4'; referencia: string; monto: number; precioCompra: number; motivo?: string }): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${this.apiUrl}wallet-ves/referencias-ves/registrar`,
          payload,
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  approveReferenciaVes(id: string): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${this.apiUrl}wallet-ves/referencias-ves/${id}/approve`,
          {},
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /**
   * Movimientos N58 contra los que se puede emparejar una referencia.
   *
   * Sólo N58: el backend responde 422 para Banco Plaza y R4, que conservan su
   * flujo original.
   */
  getCandidatosN58(id: string): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<any>(
          `${this.apiUrl}wallet-ves/referencias-ves/${id}/candidatos-n58`,
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /**
   * Empareja a mano la referencia con un movimiento N58 elegido por el operador.
   * Acredita al cliente y concilia el movimiento en la misma transaccion.
   */
  conciliarN58Manual(
    id: string,
    movimientoId: number,
    precioCompra?: number,
  ): Observable<any> {
    const payload: any = { movimientoId };
    if (precioCompra !== undefined && precioCompra !== null) {
      payload.precioCompra = precioCompra;
    }

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${this.apiUrl}wallet-ves/referencias-ves/${id}/conciliar-n58`,
          payload,
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  rejectReferenciaVes(id: string, motivoRechazo: string): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<any>(
          `${this.apiUrl}wallet-ves/referencias-ves/${id}/reject`,
          { reason: motivoRechazo },
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  getTransactions(
    params: VesTransactionsQueryParams = {},
  ): Observable<VesTransactionsResponse> {
    this.loadingSubject.next(true);

    let httpParams = new HttpParams();
    if (params.page) httpParams = httpParams.set('page', params.page.toString());
    if (params.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params.userId) httpParams = httpParams.set('userId', params.userId);
    if (params.type) httpParams = httpParams.set('type', params.type);
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.startDate) httpParams = httpParams.set('startDate', params.startDate);
    if (params.endDate) httpParams = httpParams.set('endDate', params.endDate);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get<VesTransactionsResponse>(
          `${this.apiUrl}wallet-ves/transactions`,
          { headers, params: httpParams },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        this.loadingSubject.next(false);
      }),
    );
  }
}
